import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import PageContainer from '@/components/ui/containers/PageContainer'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  listMeetingLinks,
  selectMeetingLinkById,
  selectMeetingLinks,
  selectMeetingLinkSearchResult,
  selectMeetingLinkUpdateRequestTracker,
  updateMeetingLink,
} from '@/redux/reducers/meeting-link.reducer'

import MeetingLinksTable from './components/MeetingLinksTable'
import MeetingLinkDrawer, { MeetingLinkFormValues } from './components/MeetingLinkDrawer'

export function MeetingLinksPage() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [selectedMeetingLinkId, setSelectedMeetingLinkId] = useState<string>()

  const meetingLinks = useAppSelector(selectMeetingLinks)
  const meetingLinkSearchResult = useAppSelector(selectMeetingLinkSearchResult)
  const selectedMeetingLink = useAppSelector((state) => (selectedMeetingLinkId ? selectMeetingLinkById(state, selectedMeetingLinkId) : undefined))

  const updateMeetingLinkRequestHandler = useRequestHandler({
    onSuccess: () => { },
    onError: () => { },
    requestTrackerSelector: selectMeetingLinkUpdateRequestTracker,
  })

  const meetingLinksData = useMemo(
    () =>
      meetingLinks.map((meetingLink) => ({
        id: meetingLink._id,
        name: meetingLink.name,
        isPrioritized: meetingLink.isPrioritized || false,
      })),
    [meetingLinks]
  )

  const selectedMeetingLinkInitialValues = useMemo(
    () => ({
      description: selectedMeetingLink?.description || '',
      isPrioritized: selectedMeetingLink?.isPrioritized || false,
    }),
    [selectedMeetingLink]
  )

  useEffect(() => {
    dispatch(listMeetingLinks({}))
  }, [dispatch])

  const handlePageChange = (page: number, rowsPerPage: number) => {
    dispatch(listMeetingLinks({}, { limit: rowsPerPage, skip: (page - 1) * rowsPerPage }))
  }

  const handleSelectMeetingLink = (meetingLinkId: string) => {
    setSelectedMeetingLinkId(meetingLinkId)
  }

  const handleUpdateMeetingLink = (values: MeetingLinkFormValues) => {
    if (!selectedMeetingLinkId) return

    updateMeetingLinkRequestHandler.dispatch(updateMeetingLink(selectedMeetingLinkId, values))
  }

  return (
    <PageContainer>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="h2" sx={{ mb: '30px' }}>
          {t('page.meetingLinks.title')}
        </Typography>
      </Stack>
      <MeetingLinksTable
        data={meetingLinksData}
        onPageChange={handlePageChange}
        onSelect={handleSelectMeetingLink}
        totalCount={meetingLinkSearchResult.totalCount || meetingLinks.length}
      />
      <MeetingLinkDrawer
        isUpdating={updateMeetingLinkRequestHandler.requestTracker.loading}
        initialValues={selectedMeetingLinkInitialValues}
        onClose={() => setSelectedMeetingLinkId(undefined)}
        onSubmit={handleUpdateMeetingLink}
        open={!!selectedMeetingLink}
        title={selectedMeetingLink?.name || ''}
      />
    </PageContainer>
  )
}

export default MeetingLinksPage
